import { faArrowRight, faWindowClose } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FC, CSSProperties, ReactNode } from 'react';
import { useState } from 'react';
import styled, { css } from 'styled-components';
import { Stack } from 'src/components/layout/stack';
import { useIsEnd2EndTest } from 'src/hooks/use-is-e2e-test';
import { useMeasure } from 'src/hooks/use-measure';
import { useSafeLocalStorage } from 'src/hooks/use-storage';
import { Colors } from 'src/styles';

const arrowWidth = '1rem';

type TourStep = {
  title: string;
  content: ReactNode;
  active: boolean;
  isLast: boolean;
  next: () => void;
  exit?: () => void;
};

type TourData<StepConfigs extends readonly StepConfig[]> = {
  -readonly [Index in keyof StepConfigs]: TourStep;
};

export type StepConfig = {
  title: string;
  content: ReactNode;
};

type TourProps<StepConfigs extends readonly StepConfig[]> = {
  steps: StepConfigs;
  storageKey: string;
  children: (tourData: TourData<StepConfigs>) => ReactNode;
};

/**
 * This is a rather simple tour component. E.g. it will not work well
 * for cases that span multiple pages.
 */
export const Tour = <Steps extends readonly StepConfig[]>({
  steps,
  storageKey,
  children,
}: TourProps<Steps>) => {
  const safeLocalStorage = useSafeLocalStorage();
  const isEnd2EndTest = useIsEnd2EndTest();

  const isTourActive = !safeLocalStorage.getItem(storageKey) && !isEnd2EndTest;

  const [tourData, setTourData] = useState(
    () =>
      steps.map((step, index) => {
        const active = index === 0 && isTourActive;
        const tourStep: TourStep = {
          title: step.title,
          content: step.content,
          active,
          isLast: index === steps.length - 1,
          next: () =>
            setTourData((prev) => {
              const currentIndex = prev.findIndex((step) => step.active);
              const currentStep = prev[currentIndex];
              const nextStep = prev[currentIndex + 1];
              prev[currentIndex] = { ...currentStep, active: false };
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              if (nextStep)
                prev[currentIndex + 1] = { ...nextStep, active: true };
              else safeLocalStorage.setItem(storageKey, 'true');
              return [...prev] as TourData<Steps>;
            }),
        };
        return tourStep;
      }) as TourData<Steps>
  );

  return children(tourData);
};

type TourTipProps = {
  step: TourStep;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
  /**
   * Whether the tour tip targets an inline element.
   * @default false
   */
  inline?: boolean;
  /**
   * The placement of the tour tip relative to the target element.
   * @default 'left'
   */
  placement?: 'left' | 'right';
  /**
   * The background color of the tour tip.
   * @default 'transparent'
   */
  background?: 'transparent' | 'white';
};

export const TourTip: FC<TourTipProps> = ({
  step,
  className,
  style,
  children,
  inline = false,
  placement = 'left',
  background = 'transparent',
}) => {
  const [contentRef, contentBounds] = useMeasure();
  const [messageBoxRef, messageBoxBounds] = useMeasure<HTMLButtonElement>();

  if (!step.active)
    return (
      <div
        className={className}
        style={{ display: inline ? 'inline-block' : 'block', ...style }}
      >
        {children}
      </div>
    );

  return (
    <div
      style={{ display: inline ? 'inline-block' : 'block' }}
      onClick={(e) => {
        e.stopPropagation();
        if (step.exit) step.exit();
        else step.next();
      }}
    >
      <div
        style={{
          position: 'relative',
          zIndex: 5,
        }}
      >
        <MessageBox
          ref={messageBoxRef}
          data-testid="tooltip-message"
          placement={placement}
          onClick={(e) => {
            e.stopPropagation();
            step.next();
          }}
          messageBoxHeight={messageBoxBounds.height}
          style={{
            top: `calc(${contentBounds.height}px / 2)`,
            right:
              placement === 'left'
                ? `calc(${contentBounds.width}px + ${arrowWidth})`
                : `calc(-${messageBoxBounds.width}px - ${arrowWidth})`,
          }}
        >
          <ClosingIcon
            onClick={(e) => {
              e.stopPropagation();
              if (step.exit) step.exit();
              else step.next();
            }}
          >
            <FontAwesomeIcon icon={faWindowClose} />
          </ClosingIcon>

          <Stack>
            <strong>{step.title}</strong>
            <span>{step.content}</span>
          </Stack>

          <Stack flow="column" gap={0.5} justifyContent="end">
            <strong>{step.isLast ? 'Done' : 'Next'}</strong>
            <FontAwesomeIcon icon={faArrowRight} />
          </Stack>
        </MessageBox>

        <div
          ref={contentRef}
          className={className}
          style={{
            pointerEvents: 'none',
            border: `0.3rem solid ${Colors.brandSkyBlue}`,
            background,
            ...style,
          }}
        >
          {children}
        </div>
      </div>
      <div
        style={{
          position: 'fixed',
          zIndex: 4,
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      />
    </div>
  );
};

const MessageBox = styled.button<{
  placement: 'left' | 'right';
  messageBoxHeight: number;
}>`
  border: none;
  text-align: left;

  cursor: pointer;
  position: absolute;
  padding: 1rem;
  color: #fff;
  z-index: 1;
  font-size: 1.4rem;
  width: 27rem;
  background-color: ${Colors.brandSkyBlue};
  border-radius: 0.4rem;
  box-shadow: 0 0.3rem 0.6rem #00378b33;
  background-clip: padding-box;
  transform: translateY(-50%);

  &::after {
    content: '';
    position: absolute;
    top: ${({ messageBoxHeight }) => messageBoxHeight / 2}px;
    transform: translateY(-50%);
    margin: 0 auto;
    width: 0;
    height: 0;
    ${({ placement }) =>
      placement === 'left'
        ? css`
            left: 27rem;
            border-left: ${arrowWidth} solid ${Colors.brandSkyBlue};
          `
        : css`
            left: -${arrowWidth};
            border-right: ${arrowWidth} solid ${Colors.brandSkyBlue};
          `}
    border-top: 0.6rem solid transparent;
    border-bottom: 0.6rem solid transparent;
  }
`;

const ClosingIcon = styled.div`
  border-radius: 5rem;
  position: absolute;
  left: 25rem;
  top: 0.5rem;

  &:hover {
    cursor: pointer;
  }
`;
